import {Component, ElementRef, ViewEncapsulation, WritableSignal, signal, InputSignal, input, ChangeDetectionStrategy, viewChild, Signal, EffectRef, effect, computed, inject, DestroyRef} from '@angular/core';
import {SafeHtml} from "@angular/platform-browser";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {TranslateDirective} from "../../directives/translate.directive";

@Component({
    selector: 'app-content',
    imports: [
        TranslatePipe,
        TranslateDirective
    ],
    templateUrl: './content.component.html',
    styleUrl: './content.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentComponent {
  private hostElement: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private resizeObserver?: ResizeObserver;
  public collapsible: InputSignal<boolean> = input<boolean>(false);
  public truncateLines: InputSignal<1 | 2 | 3 | 4 | null> = input<1 | 2 | 3 | 4 | null>(null);

  public textContainer: Signal<ElementRef<HTMLSpanElement> | undefined> = viewChild<ElementRef<HTMLSpanElement>>('textContainer');

  public type: InputSignal<'default' | 'richtext'> = input<'default' | 'richtext'>('default');
  public innerHTML: InputSignal<SafeHtml> = input<SafeHtml>('');
  public expandable: InputSignal<boolean> = input<boolean>(false);
  public fontSize: InputSignal<'fs-1' | 'fs-2' | 'fs-3' | 'fs-4' | 'fs-5' | 'fs-6' | 'fs-normal' | 'fs-small' | 'fs-verysmall' | 'fs-default' | null> = input<'fs-1' | 'fs-2' | 'fs-3' | 'fs-4' | 'fs-5' | 'fs-6' | 'fs-normal' | 'fs-small' | 'fs-verysmall' | 'fs-default' | null>(null);
  public fontColor: InputSignal<'text-muted' | 'text-white' | 'text-primary' | 'text-secondary' | 'text-light' | 'text-danger' | 'text-success' | 'text-warning' | 'text-orange' | 'chat-own-fg' | 'chat-other-fg' | 'chat-other-fg-muted' | 'chat-own-fg-muted' | 'chat-own-reply-name' | 'chat-other-reply-name' | 'chat-own-reply-text' | 'chat-other-reply-text' | null> = input<'text-muted' | 'text-white' | 'text-primary' | 'text-secondary' | 'text-light' | 'text-danger' | 'text-success' | 'text-warning' | 'text-orange' | 'chat-own-fg' | 'chat-other-fg' | 'chat-other-fg-muted' | 'chat-own-fg-muted' | 'chat-own-reply-name' | 'chat-other-reply-name' | 'chat-own-reply-text' | 'chat-other-reply-text' | null>(null);
  public fontDecoration: InputSignal<'fst-italic' | 'fw-bold' | 'fw-semibold' | null> = input<'fst-italic' | 'fw-bold' | 'fw-semibold' | null>(null);
  // eslint-disable-next-line @angular-eslint/no-input-rename
  public cssClass: InputSignal<string> = input<string>('', {alias: 'class'});

  public truncateClassName: Signal<string | null> = computed(() => {
    const lines = this.truncateLines();

    if (lines === null) {
      return null;
    }

    if (this.expandable() && this.isExpanded()) {
      return null;
    }

    const truncateClasses: Record<1 | 2 | 3 | 4, string> = {
      1: 'truncate-text-one-line',
      2: 'truncate-text-two-lines',
      3: 'truncate-text-three-lines',
      4: 'truncate-text-four-lines'
    };

    return truncateClasses[lines];
  });

  public richtextClassName: Signal<string> = computed(() => {
    return [
      this.fontSize(),
      this.fontColor(),
      this.fontDecoration(),
      this.cssClass(),
      this.truncateClassName()
    ].filter(Boolean).join(' ');
  });

  public defaultClassName: Signal<string> = computed(() => {
    return [
      this.fontSize() ?? 'fs-5',
      this.fontColor(),
      this.fontDecoration(),
      this.cssClass(),
      this.truncateClassName()
    ]
      .filter(Boolean)
      .join(' ');
  });

  public isExpanded: WritableSignal<boolean> = signal(false);
  public showReadMore: WritableSignal<boolean> = signal(false);
  private pendingReadMoreFrame: number | null = null;

  public constructor() {
    this.destroyRef.onDestroy(() => {
      this.cancelReadMoreMeasurement();
    });
  }

  private contentChangeEffect: EffectRef = effect(() => {
    if (!this.expandable()) {
      this.showReadMore.set(false);
      this.cancelReadMoreMeasurement();
      return;
    }

    if (this.type() !== 'richtext') {
      this.showReadMore.set(false);
      this.cancelReadMoreMeasurement();
      return;
    }

    this.innerHTML();
    const element = this.textContainer()?.nativeElement;
    if (!element) {
      this.showReadMore.set(false);
      return;
    }

    this.scheduleReadMoreMeasurement();
  });

  public expand(): void {
    if (this.expandable()) {
      this.isExpanded.set(true);
      this.showReadMore.set(false);
    }
  }

  public collapse(): void {
    if (this.collapsible()) {
      this.isExpanded.set(false);
      this.scheduleReadMoreMeasurement();
    }
  }

  private scheduleReadMoreMeasurement(): void {
    this.cancelReadMoreMeasurement();
    this.pendingReadMoreFrame = requestAnimationFrame(() => {
      this.pendingReadMoreFrame = null;
      const element = this.textContainer()?.nativeElement;
      if (!element || !this.expandable() || this.type() !== 'richtext') {
        this.showReadMore.set(false);
        return;
      }

      const hasOverflow = element.scrollHeight > element.clientHeight + 4;
      this.showReadMore.set(hasOverflow);
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => {
        const stillHasOverflow = element.scrollHeight > element.clientHeight + 4;
        this.showReadMore.set(stillHasOverflow);
      });

      this.resizeObserver.observe(element);
    });
  }

  private cancelReadMoreMeasurement(): void {
    if (this.pendingReadMoreFrame !== null) {
      cancelAnimationFrame(this.pendingReadMoreFrame);
      this.pendingReadMoreFrame = null;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }
}
